'use server'

import { apiRequest, ApiResponse } from '../request'

export type LeadStatus =
    | 'NEW'
    | 'CONTACTING'
    | 'MQL'
    | 'SAL'
    | 'SQL'
    | 'PROPOSAL'
    | 'NEGOTIATION'
    | 'CONVERTED'
    | 'INACTIVE'
    | 'LOST'

export type LeadSource =
    | 'WHATSAPP'
    | 'INSTAGRAM'
    | 'MESSENGER'
    | 'MANUAL'
    | 'WEBSITE'
    | 'REFERRAL'
    | 'CAMPAIGN'
    | 'UTM'
    | 'SOCIAL_MEDIA'
    | 'PHONE'
    | 'EMAIL'
    | 'PARTNER'
    | 'OTHER'
    | 'DEX_TALK'

export type LeadLifecycleStatus =
    | 'NEW'
    | 'CONTACTING'
    | 'MQL'
    | 'SAL'
    | 'SQL'
    | 'PROPOSAL'
    | 'NEGOTIATION'
    | 'CONVERTED'
    | 'INACTIVE'
    | 'LOST'

export type LeadSentimentType =
    | 'INTERESTED'
    | 'VERY_SATISFIED'
    | 'SATISFIED'
    | 'NEUTRAL'
    | 'CURIOUS'
    | 'HESITANT'
    | 'DISSATISFIED'
    | 'FRUSTRATED'
    | 'ANGRY'
    | 'URGENT'

export type LeadTemperature = 'HOT' | 'WARM' | 'COLD' | 'FROZEN'

export interface LeadSentiment {
    id: number
    leadId: number
    sentiment: LeadSentimentType
    confidence: number
    source: string
    notes?: string
    analyzedAt: string
    createdAt: string
}

export interface LeadActivity {
    id: number
    leadId: number
    type: string
    description: string
    userId?: number
    user?: {
        id: number
        name: string
    }
    metadata?: any
    createdAt: string
}

export interface Lead {
    id: number
    companyId: number
    name: string
    phoneNumber: string
    email?: string
    source: LeadSource
    status: LeadStatus
    lifecycleStatus: LeadLifecycleStatus
    temperature: LeadTemperature
    currentSentiment?: LeadSentimentType
    score: number
    notes?: string
    metadata?: any
    partnerId?: number
    campaignId?: number
    clientId?: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    assignedUserId?: number
    assignedUser?: {
        id: number
        name: string
    }
    // Carteira do cliente vinculado — o lead não tem carteira própria.
    convertedClient?: {
        id: number
        name: string
        walletId?: number | null
        wallet?: {
            id: number
            name: string
        }
    }
    // MQL/SAL/SQL
    isMQL: boolean
    isSAL: boolean
    isSQL: boolean
    // BANT
    hasBudget: boolean
    isDecisionMaker: boolean
    hasNeed: boolean
    expectedTimeline?: string
    qualificationNotes?: string
    // UTM
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmTerm?: string
    utmContent?: string
    // Extra
    leadCompanyName?: string
    position?: string
    leadWebsite?: string
    socialProfiles?: any
    tags: string[]
    customFields?: any
    // Relations
    deals?: any[]
    /** Conversas do lead (detalhe), a mais recente primeiro. */
    chats?: {
        id: number
        name?: string | null
        phoneNumber?: string | null
        status: string
        channel?: string
        createdAt: string
        updatedAt: string
        currentDepartment?: { id: number; name: string } | null
    }[]
    sentimentHistory?: LeadSentiment[]
    convertedAt?: string
    lastContactAt?: string
    deleted: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateLeadData {
    name: string
    phoneNumber: string
    email?: string
    source?: LeadSource
    companyId?: number
    notes?: string
    metadata?: any
    partnerId?: number
    campaignId?: number
    lifecycleStatus?: LeadLifecycleStatus
    temperature?: LeadTemperature
    isMQL?: boolean
    isSAL?: boolean
    isSQL?: boolean
    hasBudget?: boolean
    isDecisionMaker?: boolean
    hasNeed?: boolean
    expectedTimeline?: string
    qualificationNotes?: string
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmTerm?: string
    utmContent?: string
    leadCompanyName?: string
    position?: string
    leadWebsite?: string
    socialProfiles?: any
    tags?: string[]
    customFields?: any
    assignedUserId?: number
}

export interface UpdateLeadData extends Partial<CreateLeadData> {
    status?: LeadStatus
}

export interface LeadFilters {
    status?: LeadStatus
    lifecycleStatus?: LeadLifecycleStatus
    currentSentiment?: LeadSentimentType
    temperature?: LeadTemperature
    /** Carteira do cliente vinculado. */
    walletId?: number
    /** Somente leads sem cliente (pool de triagem). */
    unassignedOnly?: boolean
    assignedUserId?: number
    partnerId?: number
    campaignId?: number
    minScore?: number
    maxScore?: number
    search?: string
    /**
     * Funil da OPORTUNIDADE do lead — o lead não tem funil próprio. Traz quem tem ao
     * menos uma oportunidade não excluída nesse funil.
     */
    pipelineId?: number
    /** Etapa do funil, no mesmo vínculo de `pipelineId`. */
    stageId?: number
    tags?: string
    startDate?: string
    endDate?: string
}

export interface AssignLeadData {
    assignedUserId?: number
}

export interface ConvertLeadData {
    name?: string
    email?: string
    cpf?: string
}

// Leads

export interface LeadsPage {
    items: Lead[]
    total: number
    page: number
    limit: number
}

export async function getLeads(
    filters?: LeadFilters & { page?: number; limit?: number }
): Promise<ApiResponse<LeadsPage>> {
    return apiRequest<LeadsPage>('sales/leads', { params: filters })
}

export async function getLead(id: number): Promise<ApiResponse<Lead>> {
    return apiRequest<Lead>(`sales/leads/${id}`, {})
}

export async function createLead(
    data: CreateLeadData
): Promise<ApiResponse<Lead>> {
    return apiRequest<Lead>('sales/leads', { data }, 'post')
}

export async function updateLead(
    id: number,
    data: UpdateLeadData
): Promise<ApiResponse<Lead>> {
    return apiRequest<Lead>(`sales/leads/${id}`, { data }, 'patch')
}

export async function deleteLead(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`sales/leads/${id}`, {}, 'delete')
}

export async function convertLead(
    id: number,
    data?: ConvertLeadData
): Promise<ApiResponse<Lead>> {
    return apiRequest<Lead>(`sales/leads/${id}/convert`, { data }, 'patch')
}

export async function assignLead(
    id: number,
    data: AssignLeadData
): Promise<ApiResponse<Lead>> {
    return apiRequest<Lead>(`sales/leads/${id}/assign`, { data }, 'patch')
}

export async function getLeadTimeline(
    id: number
): Promise<ApiResponse<LeadActivity[]>> {
    return apiRequest<LeadActivity[]>(`sales/leads/${id}/timeline`, {})
}

export async function getLeadSentiments(
    id: number
): Promise<ApiResponse<LeadSentiment[]>> {
    return apiRequest<LeadSentiment[]>(`sales/leads/${id}/sentiments`, {})
}

// Labels

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
    NEW: 'Novo',
    CONTACTING: 'Contactando',
    MQL: 'MQL - Marketing Qualified',
    SAL: 'SAL - Sales Accepted',
    SQL: 'SQL - Sales Qualified',
    PROPOSAL: 'Proposta',
    NEGOTIATION: 'Negociação',
    CONVERTED: 'Convertido',
    INACTIVE: 'Inativo',
    LOST: 'Perdido',
}

export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
    NEW: 'badge-info',
    CONTACTING: 'badge-secondary',
    MQL: 'badge-info',
    SAL: 'badge-warning',
    SQL: 'badge-success',
    PROPOSAL: 'badge-secondary',
    NEGOTIATION: 'badge-accent',
    CONVERTED: 'badge-success',
    INACTIVE: 'badge-ghost',
    LOST: 'badge-error',
}

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
    WHATSAPP: 'WhatsApp',
    INSTAGRAM: 'Instagram',
    MESSENGER: 'Messenger',
    MANUAL: 'Manual',
    WEBSITE: 'Website',
    REFERRAL: 'Indicação',
    CAMPAIGN: 'Campanha',
    UTM: 'UTM',
    SOCIAL_MEDIA: 'Redes Sociais',
    PHONE: 'Telefone',
    EMAIL: 'E-mail',
    DEX_TALK: 'Dex',
    PARTNER: 'Parceiro',
    OTHER: 'Outro',
}

export const LEAD_LIFECYCLE_LABELS: Record<LeadLifecycleStatus, string> = {
    NEW: 'Novo',
    CONTACTING: 'Contactando',
    MQL: 'MQL - Marketing Qualified',
    SAL: 'SAL - Sales Accepted',
    SQL: 'SQL - Sales Qualified',
    PROPOSAL: 'Proposta',
    NEGOTIATION: 'Negociação',
    CONVERTED: 'Convertido',
    INACTIVE: 'Inativo',
    LOST: 'Perdido',
}

export const LEAD_SENTIMENT_LABELS: Record<LeadSentimentType, string> = {
    INTERESTED: 'Interessado',
    VERY_SATISFIED: 'Muito Satisfeito',
    SATISFIED: 'Satisfeito',
    NEUTRAL: 'Neutro',
    CURIOUS: 'Interessado',
    HESITANT: 'Hesitante',
    DISSATISFIED: 'Insatisfeito',
    FRUSTRATED: 'Frustrado',
    ANGRY: 'Irritado',
    URGENT: 'Urgente',
}

export const LEAD_TEMPERATURE_LABELS: Record<LeadTemperature, string> = {
    HOT: 'Quente',
    WARM: 'Morno',
    COLD: 'Frio',
    FROZEN: 'Congelado',
}

export const LEAD_TEMPERATURE_COLORS: Record<LeadTemperature, string> = {
    HOT: 'badge-error',
    WARM: 'badge-warning',
    COLD: 'badge-info',
    FROZEN: 'badge-ghost',
}
